import { fetchStoreLocations, fetchStoreById } from '../api/api-utils';
import { StoreLocationsParams } from '../../models/requests/queryParams/StoreLocationsParams';
import { RequestStoreLocations } from '../../models/requests/RequestStoreLocations';
import { getStoreLocationsParams } from '../../models/requests/queryParams/StoreLocationsParams';
import { Store } from '../../models/stores/Store';
import { StoreLocationsResponse } from '../../models/requests/responses/StoreLocationsResponse';
import { fromError } from 'zod-validation-error';
import { brandService } from './brandService';

export type StoresCallback = (stores: Store[]) => void;
export type SelectedStoreCallback = (store: Store | undefined) => void;

class StoreQuery {
  constructor(private stores: Store[], private readonly enabledBrands: number[] = []) {
    this.stores = [...stores];
  }

  public get(): Store[] {
    return this.stores;
  }

  public sort(comparator: (a: Store, b: Store) => number): StoreQuery {
    this.stores = this.stores.sort(comparator);
    return this;
  }

  public sortByDistance(): StoreQuery {
    return this.sort((a, b) => a.distance - b.distance);
  }

  public sortByDistanceDesc(): StoreQuery {
    return this.sort((a, b) => b.distance - a.distance);
  }

  public filter(predicate: (store: Store) => boolean): StoreQuery {
    this.stores = this.stores.filter(predicate);
    return this;
  }

  public filterWithoutBrands(excludedBrandIds: number[]): StoreQuery {
    if (excludedBrandIds.length === 0) {
      return this; // No brands to exclude, return all stores
    }

    return this.filter((store) => {
      let storeBrandIds = store.crmAddress?.storeLocatorBrandIds || [];
      if (this.enabledBrands.length > 0) {
        storeBrandIds = storeBrandIds.filter(brandId => this.enabledBrands.includes(brandId));
      }

      if (storeBrandIds.length === 0) return true;

      return !storeBrandIds.every(brandId =>
        excludedBrandIds.includes(brandId)
      );
    });
  }

  public filterWithBrands(allowedBrandIds: number[]): StoreQuery {

    if (allowedBrandIds.length === 0) {
      return this; // No brands specified, return all stores
    }

    return this.filter((store) => {
      let storeBrandIds = store.crmAddress?.storeLocatorBrandIds || [];

      if (this.enabledBrands.length > 0) {
        storeBrandIds = storeBrandIds.filter(brandId => this.enabledBrands.includes(brandId));
      }

      if (storeBrandIds.length === 0) return true;

      return storeBrandIds.some(brandId =>
        allowedBrandIds.includes(brandId));
    });
  }

  public filterMaxDistance(maxDistance: number): StoreQuery {
    if (maxDistance <= 0) {
      return this;
    }
    return this.filter((store) => store.distance <= maxDistance);
  }

  public filterByMaxCount(maxCount: number): StoreQuery {
    if (maxCount <= 0) {
      return this;
    }
    this.stores = this.stores.slice(0, maxCount);
    return this;
  }
}

class StoreService {
  private static instance: StoreService;
  private api!: RequestStoreLocations;
  private abortController!: AbortController;
  private existingStores: Store[] = [];
  private storesSubscribers: StoresCallback[] = [];
  private selectedStore?: Store;
  private selectedStoreSubscribers: SelectedStoreCallback[] = [];
  private enabledBrands: string[] = [];

  private constructor() { }

  public static getInstance(): StoreService {
    if (!StoreService.instance) {
      StoreService.instance = new StoreService();
    }
    return StoreService.instance;
  }

  public init(aicoApiUrl: string, aicoApiKey: string, enabledBrands: string[] = []): void {
    if (aicoApiKey === '') {
      aicoApiKey = process.env.AICO_API_KEY || '';
    }
    if (aicoApiUrl === '') {
      aicoApiUrl = process.env.AICO_API_URL || '';
    }
    this.api = RequestStoreLocations.parse({
      baseUrl: aicoApiUrl,
      bearerToken: aicoApiKey,
    });

    this.enabledBrands = enabledBrands;
  }

  public async fetchStores(
    center: google.maps.LatLngLiteral,
    radius: number = 50,
    recursiveCall: number = 0
  ): Promise<void> {
    try {
      let page = 0;
      const perPage = 50;

      while (true) {
        const params: StoreLocationsParams = getStoreLocationsParams(
          page,
          perPage,
          center.lat,
          center.lng,
          radius,
          'Store Locator',
          false,
          this.enabledBrands.length > 0 ? this.enabledBrands : undefined
        );
        const response: StoreLocationsResponse = await this.fetchStorePage(params);

        if (response.data.length === 0) {
          if (page == 0 && recursiveCall < 2) {
            await this.fetchStores(center, radius + 100, recursiveCall + 1);
            break;
          }
          // If no new stores are fetched, still update distances for existing ones
          this.updateAndMergeStores([], center);
          break;
        }

        this.updateAndMergeStores(
          this.validateStores(response),
          center
        );

        page++;
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[aico-store-locator] Error fetching stores:', error);
        throw error;
      }
    }
  }

  public async fetchStore(storeId: number): Promise<Store> {
    const response: Store = await fetchStoreById(this.api, storeId);
    return response;
  }

  /**
   * Allow outside code to abort the current fetchStores request.
   * ex: map center changes, user types in search bar
   * @param reason Optional reason for aborting the fetchStores request.
   */
  public abortStoreFetch(reason?: string) {
    this.abortController?.abort(reason);
  }

  private async fetchStorePage(
    params: StoreLocationsParams,
  ): Promise<StoreLocationsResponse> {
    this.abortController?.abort("[aico-store-locator] New page request");
    this.abortController = new AbortController();

    return await fetchStoreLocations(
      this.api!,
      this.abortController!.signal,
      params
    );
  }

  private validateStores(response: StoreLocationsResponse): Store[] {
    return response.data.map((storeData) => {
      try {
        return Store.parse(storeData);
      } catch (err) {
        console.error('[aico-store-locator.zod-parse] Invalid store data received:', storeData, fromError(err));
        throw new Error('Store data validation failed');
      }
    });
  }

  private filterForNewStores(stores: Store[]): Store[] {
    return stores.filter(
      (newStore) => !this.existingStores.some((store) => store.id === newStore.id)
    );
  }

  private calculateDistance(lat: number, lon: number, center: google.maps.LatLngLiteral): number {
    if (
      typeof google !== 'undefined' &&
      google.maps &&
      google.maps.geometry &&
      google.maps.geometry.spherical
    ) {
      const point = new google.maps.LatLng(lat, lon);
      const distanceInMeters = google.maps.geometry.spherical.computeDistanceBetween(point, new google.maps.LatLng(center.lat, center.lng));
      return distanceInMeters / 1000; // Convert to kilometers
    }
    console.warn('[aico-store-locator] Google Maps Geometry library is not available. Distance calculation will return 0.');
    return 0;
  }

  private updateAndMergeStores(stores: Store[], center: google.maps.LatLngLiteral): void {
    // Update distances for all existing stores based on the current center
    const updatedExistingStores = this.existingStores.map((store) => ({
      ...store,
      distance: this.calculateDistance(store.latitude, store.longitude, center)
    }));

    // Filter for truly new stores from the fetched 'stores' array
    const newStores = this.filterForNewStores(stores).map(newStore => ({
      ...newStore,
      distance: this.calculateDistance(newStore.latitude, newStore.longitude, center)
    }));

    // Merge updated existing stores with newly fetched stores
    const mergedStores = [...updatedExistingStores, ...newStores];

    // Remove duplicates by ID, prioritizing the newer (or existing updated) store if there's overlap
    const uniqueMergedStoresMap = new Map<number, Store>();
    mergedStores.forEach(store => uniqueMergedStoresMap.set(store.id, store));
    this.existingStores = Array.from(uniqueMergedStoresMap.values()).sort((a, b) => a.distance - b.distance);

    this.notifyStoreSubscribers();
  }

  public getStores(): Store[] {
    return this.existingStores.slice(); // Return a shallow copy to prevent external modification
  }

  public getStoresQuery(): StoreQuery {
    return new StoreQuery(this.existingStores, this.enabledBrands.map(brand => brandService.getIdWithWebName(brand) || 0));
  }

  /**
   * Subscribes to changes in the list of stores.
   * Notifies immediately with the current stores.
   * Accepts an optional AbortSignal to automatically unsubscribe when aborted.
   * @param callback A function that will be called whenever the stores change.
   * @param options Optional options containing an AbortSignal.
   * @returns An unsubscribe function.
   */
  public subscribeToStores(callback: StoresCallback, options?: { signal?: AbortSignal }): () => void {
    this.storesSubscribers.push(callback);
    callback(this.existingStores); // Notify immediately with current stores

    let abortHandler: () => void;

    const unsubscribe = () => {
      this.storesSubscribers = this.storesSubscribers.filter(cb => cb !== callback);
      if (options?.signal && abortHandler) {
        options.signal.removeEventListener('abort', abortHandler);
      }
    };

    if (options?.signal) {
      if (options.signal.aborted) {
        unsubscribe(); // If already aborted, unsubscribe immediately
      } else {
        abortHandler = () => {
          unsubscribe();
        };
        options.signal.addEventListener('abort', abortHandler);
      }
    }
    return unsubscribe;
  }

  /**
   * Calls all store subscribers with the current stores.
   */
  private notifyStoreSubscribers(): void {
    this.storesSubscribers.forEach(callback => callback(this.existingStores));
  }

  public getSelectedStore(): Store | undefined {
    return this.selectedStore;
  }

  public setSelectedStore(store?: Store): void {
    this.selectedStore = store;
    this.notifySelectedStoreSubscribers();
  }

  /**
   * Subscribes to changes on the selected store.
   * Notifies immediately with the current store.
   * Accepts an optional AbortSignal to automatically unsubscribe when aborted.
   * @param callback A function that will be called whenever the selected store changes.
   * @param notify Optional boolean to control immediate notification. Defaults to true.
   * @param options Optional options containing an AbortSignal.
   * @returns An unsubscribe function.
   */
  public subscribeToSelectedStore(callback: SelectedStoreCallback, notify: boolean = true, options?: { signal?: AbortSignal }): () => void {
    this.selectedStoreSubscribers.push(callback);
    if (notify) {
      callback(this.selectedStore);
    }

    let abortHandler: () => void;

    const unsubscribe = () => {
      this.selectedStoreSubscribers = this.selectedStoreSubscribers.filter(cb => cb !== callback);
      if (options?.signal && abortHandler) {
        options.signal.removeEventListener('abort', abortHandler);
      }
    };

    if (options?.signal) {
      if (options.signal.aborted) {
        unsubscribe();
      } else {
        abortHandler = () => {
          unsubscribe();
        };
        options.signal.addEventListener('abort', abortHandler);
      }
    }
    return unsubscribe;
  }

  /**
   * Calls all selected store subscribers with the current store.
   */
  private notifySelectedStoreSubscribers(): void {
    this.selectedStoreSubscribers.forEach(callback => callback(this.selectedStore));
  }
}

export const storeService = StoreService.getInstance();
